import { format, parseISO } from 'date-fns';
import { AlertCircle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { useOrders, getOrderRiskStatus, type OrderSummary } from '../hooks/useOrders';

/**
 * Project Summary page - displays all active orders with status indicators.
 * This is the main dashboard view for leadership.
 */
export function ProjectSummary() {
  const { data, isLoading, isError, error, refetch, isFetching } = useOrders({
    status: 'active',
  });

  if (isLoading) {
    return <ProjectSummaryLoading />;
  }

  if (isError) {
    return <ProjectSummaryError message={error.message} onRetry={() => refetch()} />;
  }

  const orders = data?.data ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Project Summary</h2>
          <p className="text-sm text-foreground-muted mt-1">
            {total} active {total === 1 ? 'project' : 'projects'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4">
          <ProjectTable orders={orders} />
        </div>
      )}
    </div>
  );
}

function ProjectTable({ orders }: { orders: OrderSummary[] }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-background-subtle border-b border-border">
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Order
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Customer
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Items
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Promised
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Expected
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {orders.map((order) => (
            <ProjectRow key={order.id} order={order} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectRow({ order }: { order: OrderSummary }) {
  const riskStatus = getOrderRiskStatus(order);

  return (
    <tr className="hover:bg-background-subtle transition-colors">
      <td className="px-4 py-3">
        <span className="font-mono text-sm">{order.gpSalesOrderNumber}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">{order.customerName}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">
          {order.lineItemCount} {order.lineItemCount === 1 ? 'item' : 'items'}
          {order.lineItemsAtRisk > 0 && (
            <span className="ml-2 text-danger text-xs">
              ({order.lineItemsAtRisk} at risk)
            </span>
          )}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">
          {formatDateRange(order.earliestPromisedShipDate, order.latestPromisedShipDate)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">
          {order.expectedShipDate ? formatDate(order.expectedShipDate) : '-'}
          {order.expectedShipDateWorst && order.expectedShipDate !== order.expectedShipDateWorst && (
            <span className="text-foreground-subtle ml-1">
              - {formatDate(order.expectedShipDateWorst)}
            </span>
          )}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={riskStatus} />
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: 'on-track' | 'at-risk' | 'late' }) {
  const config = {
    'on-track': {
      label: 'On Track',
      className: 'bg-success-subtle text-success-foreground',
      icon: CheckCircle2,
    },
    'at-risk': {
      label: 'At Risk',
      className: 'bg-warning-subtle text-warning-foreground',
      icon: Clock,
    },
    late: {
      label: 'Late',
      className: 'bg-danger-subtle text-danger-foreground',
      icon: AlertCircle,
    },
  }[status];

  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

function formatDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

function formatDateRange(start: string, end: string): string {
  if (start === end) {
    return formatDate(start);
  }
  return `${formatDate(start)} - ${formatDate(end)}`;
}

function ProjectSummaryLoading() {
  return (
    <div className="space-y-6">
      <div>
        <div className="h-8 w-48 bg-background-muted rounded animate-pulse" />
        <div className="h-4 w-32 bg-background-muted rounded animate-pulse mt-2" />
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-background-subtle border-b border-border px-4 py-3">
          <div className="h-4 w-full bg-background-muted rounded animate-pulse" />
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border last:border-0">
            <div className="h-4 w-full bg-background-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectSummaryError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-danger mb-4" />
      <h3 className="text-lg font-medium">Failed to load projects</h3>
      <p className="text-sm text-foreground-muted mt-1 max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors text-sm font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center border border-border rounded-lg">
      <CheckCircle2 className="h-12 w-12 text-foreground-muted mb-4" />
      <h3 className="text-lg font-medium">No active projects</h3>
      <p className="text-sm text-foreground-muted mt-1">
        All projects are complete or there are no orders in the system.
      </p>
    </div>
  );
}
